<template>
  <multiselect
    ref="multiselect"
    v-bind="{ ...$attrs, ...context.attributes }"
    :tabindex="Number(-1)"
    :class="`formulate-input-element formulate-input-element--${context.type} input-${size}`"
    :custom-label="customLabel"
    :data-type="context.type"
    :internal-search="false"
    :loading="isLoading"
    :options="options"
    :optionHeight="40"
    :maxHeight="limit * 40"
    :track-by="trackBy"
    :multiple="multiple"
    :placeholder="placeholder"
    :value="inputValueGetter()"
    :taggable="taggable"
    :tag-placeholder="taggable ? 'Add this as new tag' : ''"
    @tag="addTag"
    @input="inputValueSetter"
    @close="handleClose"
    @open="handleOpen"
    @pagination="handlePagination"
    @search-change="handleSearchChange"
  >
    <!-- Custom dropdown toggle -->
    <template #caret="{ toggle }">
      <div class="mc-caret__icon" @mousedown.prevent.stop="toggle">
        <i class="icon-outline_chevron-down" v-if="!isOpenDropdown"></i>
        <i class="icon-outline_close" v-else></i>
      </div>
    </template>

    <!-- Slot: after the list of options, when this element is in view, trigger pagination event -->
    <template #afterList>
      <div v-if="isLoading" class="px-2 py-2 d-flex gap-3 flex-column">
        <b-skeleton v-for="(i, idx) in ['100', '100']" :width="i" :key="idx"></b-skeleton>
      </div>
      <div v-observe-visibility="handleElementObserved" style="height: 10px" />
    </template>

    <template #selection="{ isOpen }">
      <i class="mc-fa icon-fill_search me-1" v-if="isOpen"></i>
    </template>

    <!--  For select options with image -->
    <template slot="singleLabel" slot-scope="options" v-if="hasImage">
      <div class="d-flex flex-row align-items-center">
        <img
          class="option__image"
          :src="options.option.img"
          :alt="options.option.value"
          :style="options.option.style"
        />
        <div class="option__desc ps-3">
          <span class="option__title">{{ options.option.label }}</span>
        </div>
      </div>
    </template>
    <template slot="option" slot-scope="options">
      <div
        class="d-flex flex-row justify-content-between align-items-center w-100"
        :class="{ 'option-disabled': options.option.disabled }"
      >
        <template v-if="hasImage">
          <div class="d-flex flex-row align-items-center">
            <img
              class="option__image"
              :src="options.option.img"
              :alt="options.option.value"
              :style="options.option.style"
            />
            <div class="option__desc ps-3">
              <span class="option__title">{{ options.option.label }}</span>
            </div>
          </div>
        </template>

        <template v-else-if="hasOptionMeta">
          <div class="option__desc">
            <span class="option__title">{{ customLabel(options.option) }}</span>
          </div>
          <div class="d-flex align-items-center gap-2 pe-4">
            <component
              :is="metaRightComponent"
              v-if="metaRightComponent"
              :option="options.option"
            />
          </div>
        </template>

        <template v-else>
          <div class="option__desc">
            <span class="option__title">{{ customLabel(options.option) }}</span>
          </div>
        </template>

        <div v-if="options.option.disabled" class="option__disabled-text">Tidak tersedia</div>
      </div>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { debounce } from '@/mixin/helper'

export default {
  components: {
    Multiselect,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
    initialObject: {
      type: [Object, Array],
      required: false,
    },
    trackBy: {
      type: String,
      default: 'id',
    },
    optionLabelFn: {
      type: Function,
      default: ({ item }) => item,
    },
    useInitialFallback: {
      type: Boolean,
      default: false,
    },
    optionsGetter: {
      type: [Function, null],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    tagCustom: {
      type: [Function, null],
    },
    handleChange: {
      type: Function,
      default: (option) => {
        return option
      },
    },
    handleInit: {
      type: Function,
      default: (option) => {
        return option
      },
    },
    rowItem: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    hasOptionMeta: {
      type: Boolean,
      default: false,
    },
    metaRightComponent: {
      type: [Object, String],
      default: null,
    },
  },
  data() {
    return {
      options: [],
      isLoading: false,
      isOpenDropdown: false,
      limit: 5,
      page: null,
      totalPage: null,
      totalCount: null,
      search: '',
      isPagination: true,
      selectedObjectCache: [],
    }
  },
  computed: {
    placeholder: {
      get() {
        return this.context.attributes.placeholder
      },
      set(newVal) {
        this.context.attributes.placeholder = newVal
      },
    },
    optionsContainerRef() {
      return this.$refs?.multiselect?.$refs?.list
    },
    hasNextPage() {
      if (!this.isPagination) return false
      // If totalCount is null The data has not been fetched yet
      // so we return true to get the actual total count from the first request
      if (this.totalCount == null) return true
      if (this.page == null) return false
      return this.page * this.limit < this.totalCount
    },
  },
  mounted() {
    document.addEventListener('scroll', this.repositionDropDown, true)
    this.handleInit(this.context.model)
  },
  destroyed() {
    document.removeEventListener('scroll', this.repositionDropDown)
  },
  methods: {
    addTag(newTag) {
      this.$emit('addTag', newTag)
    },

    async getData(args = { reset: false }) {
      if (this.isLoading) return
      if (!this.optionsGetter) return

      if (args.reset) {
        this.page = null
        this.totalCount = null
        this.options = []
      }

      if (!args.reset && !this.hasNextPage) return

      this.isLoading = true

      const nextPage = args.reset ? 1 : this.page + 1

      let res = await this.optionsGetter({
        limit: this.limit,
        page: nextPage,
        search: this.search,
        rowItem: this.rowItem,
        formValues: this.context.getFormValues ? this.context.getFormValues() : {},
      })

      if (this.taggable && res.data.paginated_result.length == 0) {
        res = await this.optionsGetter()
      }

      if (!res.data) return

      this.totalPage = res.metadata.total_page
      this.totalCount = res.metadata.total_count
      this.page = res.metadata.page
      this.isPagination = res.isPagination !== undefined ? res.isPagination : true

      if (args.reset) {
        this.options = res.data.paginated_result
      } else {
        this.options.push(...res.data.paginated_result)
      }

      await this.$nextTick()
      this.isLoading = false
    },
    customLabel(item) {
      if (!this.optionLabelFn) return item

      return this.optionLabelFn({ item, options: this.options })
    },
    // Since we only track the ID, we still need to pass the whole object
    // to vue-multiselect so every selection is detected
    inputValueGetter() {
      let _value = this.multiple ? [] : ''

      const selected = this.context.model
      const _initialObject = this.multiple ? this.initialObject || [] : [this.initialObject]
      const isSameTrackByValue = (source, target) => {
        if (source === target) return true
        if (!this.useInitialFallback) return false
        if (source == null || target == null) return false
        return String(source) === String(target)
      }
      let objectSource

      if (this.useInitialFallback) {
        // keep initial object as fallback when selected id is not found in current options result.
        objectSource = [...(this.options || []), ...(this.selectedObjectCache || [])]
        _initialObject.forEach((item) => {
          if (!item) return
          const hasExisting = objectSource.some(
            (opt) => opt && isSameTrackByValue(opt[this.trackBy], item[this.trackBy])
          )
          if (!hasExisting) {
            objectSource.push(item)
          }
        })
      } else {
        objectSource = [
          ...(this.options || []),
          ..._initialObject,
          ...(this.selectedObjectCache || []),
        ]
      }

      const seen = new Set()
      objectSource = objectSource.filter((item) => {
        if (!item) return false
        const key = String(item[this.trackBy])
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })

      for (let item of objectSource) {
        if (!item) continue

        if (
          this.multiple &&
          Array.isArray(selected) &&
          selected.some((selectedItem) => isSameTrackByValue(selectedItem, item[this.trackBy]))
        ) {
          _value.push(item)
        }

        // If single
        else if (isSameTrackByValue(selected, item[this.trackBy])) {
          _value = item
        }
      }

      return _value
    },
    // selected by vue-multiselect before setting it in the model
    inputValueSetter(input) {
      if (this.multiple) {
        if (Array.isArray(input)) {
          const hasDisabled = input.some((item) => item && item.disabled)
          if (hasDisabled) {
            // Revert or filter out disabled items
            input = input.filter((item) => item && !item.disabled)
          }
        }
      } else {
        if (input && input.disabled) return
      }

      const cacheSource = Array.isArray(input) ? input : [input]

      cacheSource.forEach((item) => {
        if (!item || item[this.trackBy] == null) return

        const key = String(item[this.trackBy])

        const existingIndex = this.selectedObjectCache.findIndex(
          (cacheItem) => cacheItem && String(cacheItem[this.trackBy]) === key
        )

        if (existingIndex === -1) {
          this.selectedObjectCache.push(item)
        } else {
          this.selectedObjectCache.splice(existingIndex, 1, item)
        }
      })

      const parsed = Array.isArray(input)
        ? input.map((i) => i[this.trackBy])
        : !input
        ? input
        : input[this.trackBy]

      this.context.model = parsed
      if (!this.isLoading) {
        this.handleChange(input)
      }
    },
    async handleOpen() {
      this.repositionDropDown()
      this.placeholder = this.taggable ? this.$t('global.tagText') : this.$t('global.search')
      this.getData({ reset: true })
      this.addDynamicPageHeight()
      this.$nextTick(() => {
        this.isOpenDropdown = true
      })
    },
    handleClose() {
      this.placeholder = this.context.attributes?.placeholder
      this.isLoading = false
      this.isOpenDropdown = false
      this.removeExtraPageHeight()
      this.context.blurHandler()

      const selected = this.context.model
      const hasSelection = this.multiple
        ? Array.isArray(selected) && selected.length > 0
        : selected != null && selected !== ''

      if (hasSelection) {
        this.options = this.options.filter((opt) => {
          if (!opt) return false
          if (this.multiple && Array.isArray(selected)) {
            return selected.some((s) => String(s) === String(opt[this.trackBy]))
          }
          return String(opt[this.trackBy]) === String(selected)
        })
      } else {
        this.options = []
      }

      this.page = null
      this.totalCount = null
      this.search = ''
    },
    addDynamicPageHeight() {
      const multiselectRect = this.$el.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      const spaceBelow = viewportHeight - multiselectRect.bottom

      const dropdownHeight = this.limit * 50

      if (spaceBelow < dropdownHeight) {
        const extraHeight = dropdownHeight - spaceBelow

        this.extraDiv = document.createElement('div')
        this.extraDiv.style.height = `${extraHeight}px`
        document.body.appendChild(this.extraDiv)
      }
    },
    removeExtraPageHeight() {
      if (this.extraDiv) {
        document.body.removeChild(this.extraDiv)
        this.extraDiv = null
      }
    },
    async handlePagination() {
      if (this.isLoading) return

      await this.getData()
    },
    // This function will get called when:
    // - Search value is changed
    // - Selecting an option from search (which will reset the search value)
    handleSearchChange: debounce(async function (search) {
      if (this.isLoading) return

      this.search = search
      await this.$nextTick()

      // If the dropdown is NOT open,
      // it means that user already selected an option from search
      // so we don't need to reset the options
      // since it is needed for displaying the value
      if (!this.isOpenDropdown) return

      await this.getData({ reset: true })
    }, 500),
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return
      if (this.isLoading) return

      this.handlePagination()
    },
    repositionDropDown() {
      if (!this.optionsContainerRef?.style) return
      const { top, height, left } = this.$el.getBoundingClientRect()
      this.optionsContainerRef.style.width = `${this.$el.clientWidth}px`
      this.optionsContainerRef.style.position = 'fixed'
      this.optionsContainerRef.style.bottom = 'auto'
      this.optionsContainerRef.style.top = `${top + height}px`
      this.optionsContainerRef.style.left = `${left}px`
    },
    // Methods to be called externally is in PascalCase format
    // methods to be called by parent as side effect when other fields changes
    // e.g. Delivery Form -> customer_notes should changed when product_id changed
    ResetProperties() {
      this.options = []
      this.isLoading = false
      this.limit = 5
      this.page = null
      this.totalPage = null
      this.totalCount = null
      this.search = ''
      this.selectedObjectCache = []
    },
  },
}
</script>

<style lang="scss" scoped>
@import './SelectResourcefulStyle.scss';
</style>
